//----- Component --------------------------------------------------------/
// Developer     : Guillermo Sedran
// Creation Date : 13/09/2024
// Version       : 1
// Description   : Administrador de tareas segun tipo de vehiculo
//
//------Changes --------------------------------------------------------------------- -------------//
// Change Date   :
// Change by     :
// Change description:
//
// Change Date   :
// Change by     :
// Change description:
//
//========================================================================/

import { useState, useEffect, useMemo } from "react";
import { UrlApi } from "../../../services/apirest";
import Navbar from "../../Navbar";
import { MaterialReactTable } from "material-react-table";

// TABLA EN ESPAÑOL
import { MRT_Localization_ES } from "material-react-table/locales/es";

import {
  Box,
  Dialog,
  Typography,
  IconButton,
  Button,
  ThemeProvider,
  createTheme,
  useTheme,
  Tooltip,
} from "@mui/material";
import AgregarTareaTipoVehiculos from "./AgregarTareaTipoVehiculos";
import EditarTareaTipoVehiculos from "./EditarTareaTipoVehiculos";
import CambiarEstadoTareaTipoVehiculo from "./CambiarEstadoTareaTipoVehiculo";

// ICONOS
import BlockIcon from "@mui/icons-material/Block";
import EditIcon from "@mui/icons-material/Edit";

import { logEventos } from "../../../services/services";

function TareasTipoVehiculos() {
  const globalTheme = useTheme();
  const [tareasParaTiposVehiculos, setTareasParaTiposVehiculos] = useState([]);

  //*************************************************************************************************************************************/
  // DIALOG PARA AGREGAR TAREAS
  const [openAgregarTarea, setOpenAgregarTarea] = useState(false);

  const handleOpenAgregarTarea = () => {
    setOpenAgregarTarea(true);
  };

  const handleCloseAgregarTarea = () => {
    setOpenAgregarTarea(false);
  };

  //----------------------------------------------------------------------------------------------------------------------------------------
  // DIALOG PARA EDITAR LOS DATOS
  const [openEditarTarea, setOpenEditarTarea] = useState(false);
  const [tareaSeleccionada, setTareaSeleccionada] = useState(null);

  // USANDO ROW TOMO LOS DATOS DE ESA FILA
  const handleClickOpenEditarTarea = (row) => {
    setTareaSeleccionada(row);
    setOpenEditarTarea(true);
  };

  const handleCloseEditarTarea = (row) => {
    setOpenEditarTarea(false);
  };

  //----------------------------------------------------------------------------------------------------------------------------------------
  // DIALOG PARA CAMBIAR ESTADO DE LAS TAREAS
  const [openCambiarEstadoTarea, setOpenCambiarEstadoTarea] = useState(false);

  const handleOpenCambiarEstadoTarea = (row) => {
    setTareaSeleccionada(row);
    setOpenCambiarEstadoTarea(true);
  };

  const handleCloseCambiarEstadoTarea = () => {
    setOpenCambiarEstadoTarea(false);
  };

  //*************************************************************************************************************************************/
  const [isLoading, setIsLoading] = useState(true);

  const obtenerTareasParaTipoVehiculos = () => {
    setIsLoading(true); // Mostrar indicador de carga
    fetch(UrlApi + "/tareas_tipo_vehiculos")
      .then((data) => data.json())
      .then((data) => {
        setTareasParaTiposVehiculos(data);
        setIsLoading(false); // Ocultar indicador de carga
      });
  };

  useEffect(() => {
    obtenerTareasParaTipoVehiculos();
  }, []);

  //*************************************************************************************************************************************/

  const customLocalization = {
    ...MRT_Localization_ES,
    body: {
      ...MRT_Localization_ES.body,
      noData: "No hay registros para mostrar",
    },
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "tipo_vehiculo",
        header: "Tipo",
        size: 150,
      },
      {
        accessorKey: "parte_vehiculo",
        header: "Parte Vehículo",
        size: 150,
      },
      {
        accessorKey: "tarea",
        header: "Tarea",
        size: 200,
      },
      {
        accessorKey: "frecuencia_tarea",
        header: "Frecuencia",
        size: 150,
        Cell: ({ row }) => {
          // Si tiene valor en 'frecuencia', lo muestra; de lo contrario, muestra 'frecuencia_km'
          return row.original.frecuencia
            ? row.original.frecuencia_tarea
            : `${row.original.valor_km} km`;
        },
      },
    ],
    []
  );

  const initialState = {
    density: "compact",
    showGlobalFilter: true, // Mostrar la barra de búsqueda
    grouping: ["tipo_vehiculo", "parte_vehiculo"],
    sorting: [
      {
        id: "tipo_vehiculo",
        desc: true,
      },
    ],
    pagination: { pageIndex: 0, pageSize: 10 },
  };

  const tableHeadCellProps = {
    align: "left",
    sx: {
      fontSize: 16,
      color: "white",
      fontWeight: "normal",
      backgroundColor: "#2471A3",
      borderColor: "black",
      "& .MuiSvgIcon-root": {
        color: "white", // color de los íconos
      },
    },
  };

  const muiSearchTextFieldProps = {
    placeholder: "Buscar..",
    sx: { backgroundColor: "#ffffff" },
    variant: "outlined",
    size: "small",
  };

  const tableTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: globalTheme.palette.mode,
          primary: globalTheme.palette.primary,
          info: {
            main: "rgb(255,122,0)",
          },
          background: {
            default:
              globalTheme.palette.mode === "light"
                ? "rgb(234, 239, 241)"
                : "#000",
          },
        },
        typography: {
          button: {
            textTransform: "none",
            fontSize: "1rem",
          },
        },
        components: {
          MuiTooltip: {
            styleOverrides: {
              tooltip: {
                fontSize: "0.8rem",
              },
            },
          },
        },
      }),
    [globalTheme]
  );

  // ----------------------------------------------------------------------------------

  // Función para actualizar las tareas luego de que se guarde una tarea
  const handleTareaGuardada = () => {
    obtenerTareasParaTipoVehiculos(); // Vuelve a obtener los datos actualizados
  };
  useEffect(() => {
    logEventos(
      "acceso al menú Tareas Tipo Vehículos",
      "Utilización de Vehículos",
      "Tareas Tipo Vehículos",
      localStorage.getItem("user")
    );
  }, []);

  return (
    <>
      <Navbar />
      <Box
        sx={{
          marginTop: "20px",
          marginBottom: "25px",
          marginLeft: "40px",
          marginRight: "40px",
        }}
      >
        <ThemeProvider theme={tableTheme}>
          <MaterialReactTable
            columns={columns}
            data={tareasParaTiposVehiculos}
            state={{ isLoading: isLoading }}
            enableGlobalFilter={true} // Habilitar el filtro global
            enableToolbarInternalActions={true}
            initialState={initialState}
            enableGrouping
            positionActionsColumn="last"
            autoResetPageIndex={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableColumnActions={true}
            enableColumnFilters={false} // Desactiva filtros por columna
            enableHiding={false}
            localization={customLocalization}
            muiTableHeadCellProps={tableHeadCellProps}
            muiSearchTextFieldProps={muiSearchTextFieldProps}
            muiTableBodyCellProps={({ row }) => ({
              align: "left",
              sx: {
                color: "black",
                borderColor: "black",
                wordWrap: "break-word", // Permite que el texto se divida cuando sea necesario
                whiteSpace: "normal", // Permite que el texto ocupe varias líneas
              },
            })}
            enableRowSelection={false}
            enableRowActions={true}
            renderTopToolbarCustomActions={() => (
              <>
                <Typography variant="poster" fontSize={26}>
                  Tareas Por Tipo de Vehículo
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleOpenAgregarTarea} // Abre el diálogo
                >
                  Agregar Tarea
                </Button>
              </>
            )}
            renderRowActions={({ row }) => (
              <>
                <Box sx={{ display: "flex", flexWrap: "nowrap", gap: "8px" }}>
                  <Tooltip
                    arrow
                    placement="left"
                    disableInteractive
                    title="Editar Tarea"
                  >
                    <IconButton
                      onClick={() => handleClickOpenEditarTarea(row.original)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip
                    arrow
                    placement="left"
                    disableInteractive
                    title={
                      row.original.estado === 1
                        ? "Anular Tarea"
                        : "Activar Tarea"
                    }
                  >
                    <IconButton
                      onClick={() => handleOpenCambiarEstadoTarea(row.original)}
                    >
                      <BlockIcon
                        sx={{
                          color: row.original.estado === 1 ? "red" : "green",
                        }}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              </>
            )}
            muiTableBodyRowProps={({ row }) => {
              const isEstadoActivo = row.original.estado === 1;

              // Paletas para activos (estado = 1) e inactivos (estado != 1)
              const activeColor = ["#a4f0a8"];
              const inactiveColor = ["#f5bbbbff"];

              // Colores al pasar el mouse
              const activeHover = "#d1f0d1";
              const inactiveHover = "#f3d4d4ff";

              const bgColor = isEstadoActivo ? activeColor : inactiveColor;
              const hoverColor = isEstadoActivo ? activeHover : inactiveHover;

              return {
                sx: {
                  backgroundColor: bgColor,
                  color: "black",
                  borderColor: "black",
                  transition: "background-color 0.2s ease-in-out",
                  "&:hover": isLoading
                    ? {
                        backgroundColor: bgColor + " !important",
                        "& .MuiTableCell-root": {
                          backgroundColor: bgColor + " !important",
                        },
                      }
                    : {
                        backgroundColor: hoverColor,
                        "& .MuiTableCell-root": {
                          backgroundColor: "inherit !important",
                        },
                      },
                },
              };
            }}
          />
        </ThemeProvider>
      </Box>

      {/* CREAR TAREA */}
      <Dialog
        fullWidth
        maxWidth={"md"}
        open={openAgregarTarea}
        onClose={handleCloseAgregarTarea}
      >
        <AgregarTareaTipoVehiculos
          handleCloseAgregarTarea={handleCloseAgregarTarea} // Pasa la función para cerrar el diálogo
          onTareaGuardada={handleTareaGuardada} // Recargar tabla al guardar
        />
      </Dialog>

      {/* EDITAR TAREA */}
      <Dialog
        fullWidth
        maxWidth={"md"}
        open={openEditarTarea}
        onClose={handleCloseEditarTarea}
      >
        {tareaSeleccionada && (
          <EditarTareaTipoVehiculos
            tarea={tareaSeleccionada}
            onClose={handleCloseEditarTarea}
            onTareaGuardada={handleTareaGuardada} // Recargar tabla al guardar
          />
        )}
      </Dialog>

      {/* CAMBIAR ESTADO TAREA */}
      <Dialog
        fullWidth
        maxWidth={"xs"}
        open={openCambiarEstadoTarea}
        onClose={handleCloseCambiarEstadoTarea}
      >
        {tareaSeleccionada && (
          <CambiarEstadoTareaTipoVehiculo
            tarea={tareaSeleccionada}
            onClose={handleCloseCambiarEstadoTarea} // Pasa la función para cerrar el diálogo
            onGuardarEstado={handleTareaGuardada} // Recargar tabla al guardar
          />
        )}
      </Dialog>
    </>
  );
}

export default TareasTipoVehiculos;
